// src/types/index.ts
import { Timestamp } from "firebase/firestore";

export type UserRole = "user" | "agent" | "admin";
export type UserStatus = "active" | "inactive" | "suspended" | "deleted";
export type TicketPriority = "P1" | "P2" | "P3" | "P4";
export type TicketStatus = "open" | "in_progress" | "pending_user" | "resolved" | "closed";

// Report opt-ins are opt-IN (absent = off). Ticket-lifecycle preferences
// are opt-OUT (absent = on) so existing accounts keep receiving what they
// receive today; only an explicit `false` suppresses a send. The
// admin-level toggles in Settings → Email remain a master switch on top.
export interface NotificationPreferences {
  /** End-of-day report email (agents: their work; admins: all agents). */
  dailyReport: boolean;
  /** End-of-week report email — same audience split. */
  weeklyReport: boolean;
  /** Confirmation email when I submit a ticket. */
  ticketConfirmation?: boolean;
  /** Email when an agent replies to my ticket. */
  ticketReplies?: boolean;
  /** Satisfaction survey after my ticket is resolved. */
  csatSurvey?: boolean;
  /** Agents only — email when a ticket is assigned to me. */
  agentAssignment?: boolean;
}

export interface HelpDeskUser {
  uid: string;
  email: string;
  firstName: string;
  lastName: string;
  displayName: string;
  role: UserRole;
  department: string;
  status: UserStatus;
  source: "csv" | "gws" | "manual";
  gwsId?: string;
  gwsOrgUnit?: string;
  avatarUrl?: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  lastSyncAt?: Timestamp;
  welcomeEmailSent?: boolean;
  notificationsLastReadAt?: Timestamp;
  notifications?: NotificationPreferences;
}

export interface TicketAttachment {
  name: string;
  url: string;
  storagePath: string;
  contentType: string;
  size: number;
  uploadedBy: string;
  uploadedAt: Timestamp;
}

export interface Ticket {
  id: string;
  title: string;
  description: string;
  status: TicketStatus;
  priority: TicketPriority;
  category: string;
  submitterId: string;
  submitterEmail: string;
  submitterName?: string;
  assignedTo?: string;
  assignedToName?: string;
  attachments?: TicketAttachment[];
  triageConfidence?: number;
  triageReason?: string;
  triageSuggestKB?: string;
  triagedAt?: Timestamp;
  slaBreach?: Timestamp;
  slaWarned?: boolean;
  slaWarnedAt?: Timestamp;
  slaBreachAlertedAt?: Timestamp;
  slaPausedAt?: Timestamp;
  pendingUserAskedAt?: Timestamp;
  pendingUserRoundCount?: number;
  /** Round limit in force when this ticket was last asked for info. */
  pendingUserMaxRounds?: number;
  escalatedFromPending?: boolean;
  tags?: string[];
  createdAt: Timestamp;
  updatedAt: Timestamp;
  resolvedAt?: Timestamp;
  origin?: "manual" | "ai-chat";
  originChat?: string;
  // CSAT — populated when a survey is sent / submitted. The token is
  // a 32-byte hex string used to authenticate the public submission
  // page; once a score has been submitted it is cleared (single-use).
  csatSurveyToken?: string;
  csatSurveySentAt?: Timestamp;
  csatScore?: number;        // 1-5
  csatComment?: string;
  csatSubmittedAt?: Timestamp;
}

export interface TicketMessage {
  id: string;
  authorId: string;
  authorName: string;
  authorRole: UserRole | "ai";
  body: string;
  attachments?: TicketAttachment[];
  createdAt: Timestamp;
}

export type Platform = "macos" | "windows" | "ios" | "android" | "linux" | "web";

export interface KBArticle {
  id: string;
  title: string;
  body: string;
  category: string;
  platforms?: Platform[];
  viewCount: number;
  rating: number;
  ratingCount: number;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export const PLATFORM_LABELS: Record<Platform, string> = {
  macos:   "macOS",
  windows: "Windows",
  ios:     "iOS",
  android: "Android",
  linux:   "Linux",
  web:     "Web",
};

export const ALL_PLATFORMS: Platform[] = ["macos", "windows", "ios", "android", "linux", "web"];

// ─── Deflection tracking ──────────────────────────────────────────
// A "deflection" is created when a user starts typing a ticket but
// navigates to the KB or AI Assistant instead. It only counts toward
// reporting metrics once the user confirms the alternate path resolved
// their issue.

export type DeflectionChannel = "kb" | "ai-chat";
export type DeflectionStatus = "candidate" | "resolved" | "abandoned";

export interface Deflection {
  id: string;
  userId: string;
  userEmail: string;
  userDisplayName: string;
  issueTitle: string;
  issueDescription: string;
  channel: DeflectionChannel;
  channelTarget?: string; // KB articleId or chat transcript (optional)
  status: DeflectionStatus;
  startedAt: Timestamp;
  resolvedAt?: Timestamp;
  abandonedAt?: Timestamp;
}

export interface SyncLog {
  id: string;
  runAt: Timestamp;
  source: "gws" | "csv";
  status: "success" | "warning" | "failed";
  totalUsers: number;
  created: number;
  updated: number;
  disabled: number;
  deleted: number;
  errors: string[];
  durationMs: number;
}

export interface BrandingConfig {
  logoUrl?: string;
  primaryColor: string;
  portalName: string;
  /** Optional custom domain (e.g. "ticketwhip.com"). Used by Cloud
   *  Functions to construct ticket URLs in outgoing emails. Falls back
   *  to the Firebase Hosting URL if blank. */
  customDomain?: string;
}

// Business-hours schedule. When `enabled`, SLA timers only count
// minutes that fall within working hours on working days, in the
// configured timezone, excluding listed holidays. When disabled,
// SLAs are computed against wall-clock time (24/7) — same as before
// this feature existed, so existing deployments are unaffected.
export interface BusinessSchedule {
  enabled: boolean;
  /** IANA timezone string, e.g. "America/New_York" or "Europe/London". */
  timezone: string;
  /** 0=Sunday, 1=Monday, …, 6=Saturday. Default M-F = [1,2,3,4,5]. */
  workingDays: number[];
  /** "HH:MM" 24-hour. Both inclusive of the start, exclusive at end. */
  workingHours: { start: string; end: string };
  /** Dates the helpdesk is closed, in the schedule's timezone. Format
   *  YYYY-MM-DD. Each date is treated as a full non-working day. */
  holidays: string[];
}

export interface SlaConfig {
  P1: { responseHours: number; resolutionHours: number };
  P2: { responseHours: number; resolutionHours: number };
  P3: { responseHours: number; resolutionHours: number };
  P4: { responseHours: number; resolutionHours: number };
  businessSchedule?: BusinessSchedule;
}

// ─── Integration registry ─────────────────────────────────────────
// Stored in Firestore at integrations/{id}. Each integration is a
// pluggable provider — identity, notification, or AI. Integrations
// can be enabled/disabled independently. The "manual" identity
// provider is always required.

export type IntegrationType = "identity" | "notification" | "ai";

export type IntegrationId =
  | "manual"   // identity — built-in email/password (always enabled)
  | "gws"      // identity — Google Workspace
  | "m365"     // identity — Microsoft 365 (future)
  | "slack"    // notification — Slack
  | "teams"    // notification — Microsoft Teams (future)
  | "email"    // notification — transactional email
  | "ai";      // ai — triage and chat

export interface IntegrationDoc<TConfig = unknown> {
  id: IntegrationId;
  type: IntegrationType;
  displayName: string;
  description: string;
  enabled: boolean;
  required: boolean;
  config: TConfig;
  requiredSecrets: string[];
  status?: "ready" | "needs_secrets" | "needs_config" | "error";
  statusMessage?: string;
  updatedAt?: Timestamp;
}

// ─── Per-integration config shapes ────────────────────────────────

export type AiProviderId = "gemini" | "openai" | "anthropic" | "openrouter";

/** A row from OpenRouter's model catalog, as normalised by the
 *  listOpenRouterModels callable. Prices are USD per 1M tokens. */
export interface OpenRouterModel {
  id: string;
  name: string;
  contextLength: number;
  promptPer1M: number;
  completionPer1M: number;
  isFree: boolean;
  supportsJsonMode: boolean;
}

export interface AiIntegrationConfig {
  provider: AiProviderId;
  triageModel: string;
  autoAssignThreshold: number;
  sendSummaryToUser: boolean;
  selfServiceDeflection: boolean;
  /** Times the AI may ask the submitter for more info before escalating
   *  to a human agent. Clamped to MIN/MAX_INFO_ROUNDS server-side. */
  maxInfoRounds: number;
  geminiModel?: string;
  openaiModel?: string;
  anthropicModel?: string;
  openrouterModel?: string;
  /** Recorded from the model catalog when an OpenRouter model is picked,
   *  so the backend knows whether it may send response_format. */
  openrouterJsonMode?: boolean;
}

export interface GwsIntegrationConfig {
  customerDomain: string;
  customerId: string;
  serviceAccountEmail: string;
  targetGroup?: string;
  syncTime: string;
  autoDisableSuspended: boolean;
  autoDeleteRemoved: boolean;
  syncOrgUnits: boolean;
  notifyOnErrors: boolean;
  allowedDomain: string;
}

export interface SlackIntegrationConfig {
  channels: {
    criticalTickets: string;
    allTickets: string;
    slaAlerts: string;
    syncErrors: string;
    dailyDigest: string;
  };
  triggers: {
    newTicket: boolean;
    p1Escalation: boolean;
    slaWarning: boolean;
    ticketResolved: boolean;
    dailyDigest: boolean;
    syncErrors: boolean;
  };
}

export type EmailProviderId = "resend" | "sendgrid" | "smtp";

export interface EmailIntegrationConfig {
  provider: EmailProviderId;
  fromAddress: string;
  replyTo: string;
  ticketConfirmation: boolean;
  agentAssignment: boolean;
  csatSurvey: boolean;
}

export interface ManualIntegrationConfig {
  allowSelfSignup: boolean;
  passwordMinLength: number;
}

// Stub configs — populated when implementations land
export interface M365IntegrationConfig {
  tenantId?: string;
}
export interface TeamsIntegrationConfig {
  webhookUrl?: string;
}

// ─── Secret registry metadata ─────────────────────────────────────
// Returned by listSecretsMetadata callable. Never includes secret values.

export interface SecretMetadata {
  name: string;
  description: string;
  integrationId: IntegrationId;
  exists: boolean;
  updateTime?: string; // ISO string
}

// ─── Audit log ────────────────────────────────────────────────────

export type AuditAction =
  | "secret.set"
  | "secret.delete"
  | "integration.enable"
  | "integration.disable"
  | "integration.update"
  | "user.create"
  | "user.disable"
  | "user.enable"
  | "user.delete"
  | "user.role_change"
  | "ticket.force_open";

export interface AuditLogEntry {
  id: string;
  actorUid: string;
  actorEmail: string;
  action: AuditAction;
  target: string;
  metadata?: Record<string, unknown>;
  timestamp: Timestamp;
}

// Analytics aggregate shape (written by scheduled function)
export interface AnalyticsSnapshot {
  date: string;
  totalTickets: number;
  openTickets: number;
  resolvedTickets: number;
  avgFirstResponseMs: number;
  avgResolutionMs: number;
  csatAvg: number;
  aiDeflections: number;
  slaBreaches: number;
  byCategory: Record<string, number>;
  byPriority: Record<TicketPriority, number>;
  byAgent: Record<string, { resolved: number; avgMs: number; csat: number }>;
}

export const PRIORITY_LABEL: Record<TicketPriority, string> = {
  P1: "P1 — Critical",
  P2: "P2 — High",
  P3: "P3 — Medium",
  P4: "P4 — Low",
};

export const STATUS_LABEL: Record<TicketStatus, string> = {
  open: "Open",
  in_progress: "In Progress",
  pending_user: "Pending User Info",
  resolved: "Resolved",
  closed: "Closed",
};

export const CATEGORIES = [
  "Network & VPN",
  "Hardware",
  "Software & Apps",
  "Account & Access",
  "Email / M365",
  "Other",
];
